/** /pricing: the live plan from Dodo, how the trial works, and the questions people ask before paying. */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { planPrice } from '../lib/billing';
import { Band, ClosingCta, Eyebrow, FaqList, SiteButton, SiteLayout } from './chrome';
import { longDate } from './articles';
import { PAGES, pricingFaq } from './content';
import { useFacts, usePublicPlan, useSite, type PublicPlan } from './hooks';
import { Tick } from './Landing';

const INCLUDED: [string, string][] = [
  ['General ledger', 'Multi-entity, multi-currency, double entry enforced'],
  ['Revenue and AR', 'Contracts, ASC 606 schedules, invoices, collections'],
  ['Spend and AP', 'Vendors, bills, accrual suggestions, prepaid amortization'],
  ['Cash', 'Bank matching with reasons, 13-week cash forecast'],
  ['Close and controls', 'Close checklist, continuous controls, SHA-256 hash chain'],
  ['Reports', 'Statements, SaaS metrics, deferred revenue, aging, runway'],
  ['Approvals', 'Materiality tiers and segregation of duties for people, AI and API'],
  ['Developers', 'REST API, MCP server and signed webhooks'],
];

const COMPARE: [string, string][] = [
  ['Rillet', '/compare/raremargin-vs-rillet'],
  ['QuickBooks Online', '/compare/raremargin-vs-quickbooks-online'],
  ['NetSuite', '/compare/raremargin-vs-netsuite'],
];

export function PricingPage() {
  const q = usePublicPlan();
  const site = useSite().data;
  const facts = useFacts();
  const d = q.data;
  const trialDays = d?.trialDays ?? 14;
  return (
    <SiteLayout title={PAGES['/pricing'].title}>
      <Band stage inner="!pb-10 lg:!pb-14 !pt-16 lg:!pt-24">
        <div className="text-center">
          <Eyebrow className="justify-center text-site-muted">Pricing</Eyebrow>
          <h1 className="site-h1 mx-auto mt-6 max-w-[13ch]">
            One plan. <span className="text-site-green">Every feature.</span>
          </h1>
          <p className="site-lead mx-auto mt-6 max-w-[52ch] text-site-ink-2">
            Each company gets the whole ledger, the assistant and the API. Start with {trialDays} days free; subscribe when the books are in.
          </p>
        </div>

        <div className="mx-auto mt-14 max-w-[1040px] rounded-[28px] bg-site-oat p-2 sm:rounded-[40px] sm:p-3">
          <div className="grid overflow-hidden rounded-[22px] bg-site-white shadow-[0_30px_60px_-40px_rgb(27_26_24/0.4)] ring-1 ring-black/5 sm:rounded-[32px] lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            <div className="border-b border-site-rule p-7 sm:p-10 lg:border-r lg:border-b-0">
              <div className="flex items-center justify-between gap-3">
                <span className="site-eyebrow text-site-muted">{d?.plan?.name ?? 'RareMargin'}</span>
                <span className="site-eyebrow rounded-full bg-site-lime px-2.5 py-1.5 text-site-lime-ink">{trialDays} days free</span>
              </div>
              <Price data={d} loading={q.isLoading} email={site?.supportEmail} />
              <SiteButton to="/signup" size="lg" arrow className="mt-8 w-full">
                Start your {trialDays}-day trial
              </SiteButton>
              <p className="mt-4 text-center text-[14px] text-site-muted">No card needed to start.</p>
              <dl className="mt-8 space-y-3 border-t border-site-rule pt-6 text-[14.5px]">
                <Row label="Assistant">{d ? `${d.aiMonthlyMessages.toLocaleString('en-US')} messages a month (${d.aiTrialMessages} during the trial)` : '…'}</Row>
                <Row label="Payment">By card, through Dodo Payments</Row>
                <Row label="Cancel">Any time, from Settings → Billing</Row>
                <Row label="Updated">
                  <time dateTime={PAGES['/pricing'].updated}>{longDate(PAGES['/pricing'].updated)}</time>
                </Row>
              </dl>
            </div>
            <div className="p-7 sm:p-10">
              <div className="site-eyebrow text-site-muted">Everything included</div>
              <ul className="mt-6 space-y-4">
                {INCLUDED.map(([t, detail]) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-1">
                      <Tick />
                    </span>
                    <span className="text-[15.5px] leading-snug">
                      <b className="font-[620]">{t}</b>
                      <span className="block text-[14.5px] text-site-muted">{detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Band>

      <Band inner="!py-16 lg:!py-24">
        <Eyebrow>How the trial works</Eyebrow>
        <ol className="mt-8 grid border-t-2 border-site-green md:grid-cols-3">
          {[
            ['Sign up', `Your company gets ${trialDays} days of full access and ${d?.aiTrialMessages ?? 50} assistant messages. Start from your own books or a sample company.`],
            ['Subscribe', 'An owner or admin subscribes from Settings → Billing. Days left on the trial carry over, so nothing is charged until it ends.'],
            ['Or don’t', 'When a trial ends without a subscription, the company becomes read-only. Nothing is deleted, and everyone can still read and export the books.'],
          ].map(([t, body], i) => (
            <li key={t} className="border-b border-site-rule py-7 md:border-b-0 md:border-l md:px-8 md:first:border-l-0 md:first:pl-0">
              <div className="site-eyebrow text-site-muted">Step 0{i + 1}</div>
              <div className="mt-3 font-display text-[24px] font-[580] tracking-[-0.03em]">{t}</div>
              <p className="mt-2 max-w-[38ch] text-[15.5px] leading-relaxed text-site-ink-2">{body}</p>
            </li>
          ))}
        </ol>
      </Band>

      <Band tone="white" inner="!py-16 lg:!py-24">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
          <div>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="site-h2 mt-5 !text-[clamp(30px,3.4vw,44px)]">Before you subscribe</h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-site-ink-2">
              Something else? Write to{' '}
              {site ? (
                <a className="text-site-green underline-offset-4 hover:underline" href={`mailto:${site.supportEmail}`}>
                  {site.supportEmail}
                </a>
              ) : (
                'us'
              )}
              .
            </p>
            <p className="mt-4 text-[15.5px] leading-relaxed text-site-ink-2">
              Comparing? See RareMargin vs{' '}
              {COMPARE.map(([label, to], i) => (
                <span key={to}>
                  {i > 0 && (i === COMPARE.length - 1 ? ' and ' : ', ')}
                  <Link to={to} className="text-site-green underline-offset-4 hover:underline">
                    {label}
                  </Link>
                </span>
              ))}
              .
            </p>
          </div>
          <FaqList items={pricingFaq(facts)} />
        </div>
      </Band>
      <ClosingCta />
    </SiteLayout>
  );
}

function Price({ data, loading, email }: { data?: PublicPlan; loading: boolean; email?: string }) {
  if (loading) return <div className="mt-6 h-[60px] w-48 animate-pulse rounded-lg bg-site-oat" />;
  if (!data?.plan) {
    // Billing is off on this server, or Dodo didn't answer: never show a made-up price.
    return (
      <div className="mt-6">
        <div className="font-display text-[34px] leading-none font-[580] tracking-[-0.04em]">Free for {data?.trialDays ?? 14} days</div>
        <p className="mt-3 text-[14.5px] text-site-muted">
          The subscription price is shown at checkout{email ? <>, or ask us at {email}</> : null}.
        </p>
      </div>
    );
  }
  const { amount, per } = planPrice(data.plan);
  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-display text-[64px] leading-none font-[580] tracking-[-0.05em]">{amount}</span>
        <span className="text-[16px] text-site-muted">{per}</span>
      </div>
      <p className="mt-2 text-[14.5px] text-site-muted">Per company, with no per-user fee. Taxes are added at checkout where they apply.</p>
    </div>
  );
}

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[96px_1fr] gap-3">
    <dt className="text-site-muted">{label}</dt>
    <dd className="text-site-ink">{children}</dd>
  </div>
);
